import { StyledTourDetail } from "@components";
import styled from "styled-components";
import { areAllFaresExpired, breakpoint, useWindowDimensions } from "@utilities";
import _ from "lodash";
import { IProductDetail } from "@interfaces";
import { AllFaresAvailabilityPanel } from "./AllFaresAvailabilityPanel";

interface IProps {
    product: IProductDetail;
    initialDateRange?: {
        startDate: Date;
        endDate: Date;
        selectedDate: Date;
    } | null;
    /** Defers the calendar's auto-fetch until every fare group has hydrated. */
    isProductFullyLoaded?: boolean;
}

export const ProductPrices = ({ product, initialDateRange, isProductFullyLoaded }: IProps) => {
    const { faresPrices: rawProductPrices } = product;
    const productPrices = _.orderBy(rawProductPrices || [], [(fp) => (fp.defaultFare ? 0 : 1)]);
    const { width } = useWindowDimensions();

    // A product is only "no longer available" when EVERY fare has expired.
    // Previously this checked only the first (default) fare's travelEnd, which
    // wrongly hid products that still have other valid fares (e.g. an expired
    // default fare alongside a current sale fare and a future-season fare).
    const isProductExpired = areAllFaresExpired(productPrices);

    const mobileView = isProductExpired ? (
        <NotAvailable className="noFare">This product is no longer available</NotAvailable>
    ) : (
        <MobileViewContainer>
            <AllFaresAvailabilityPanel
                product={product}
                initialDate={initialDateRange?.startDate}
                isProductFullyLoaded={isProductFullyLoaded}
            />
        </MobileViewContainer>
    );

    const desktopView = isProductExpired ? (
        <NotAvailable className="noFare">This product is no longer available</NotAvailable>
    ) : (
        <StyledTourDetail>
            <div className="heading">
                <h3>Live Availability &amp; Pricing</h3>
            </div>
            <AllFaresAvailabilityPanel
                product={product}
                initialDate={initialDateRange?.startDate}
                isProductFullyLoaded={isProductFullyLoaded}
            />
        </StyledTourDetail>
    );

    return <>{width < 768 ? mobileView : desktopView}</>;
};

const NotAvailable = styled.p`
    width: ${(props) => props.theme.widths.wide_700};
    margin: 0 auto;
    background-color: #fff;
    margin-top: 50px;
    font-size: 20px;
    font-weight: 700;
    padding: 10px 20px;
    text-align: center;
    ${breakpoint.breakIpadPro`
        width: ${(props) => props.theme.widths.wide_900};
    `}

    ${breakpoint.breakLarge`
        width: ${(props) => props.theme.widths.wide_1110};
    `}
`;

const MobileViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0;
`;
